import defer from '../utils/defer';
import { DATASTRATEGY, HTTP_CONSTANTS } from '../constants';
/**
 * @ngdoc service
 * @name proximus.core.entity:HeaderService
 * @description
 * # HeaderService
 * Manages http request headers for entities.
 */
class HeaderService {
    constructor(jwtService, languageService, platformService, mobilePlatformService, webPlatformService) {
        this.jwtService = jwtService;
        this.languageService = languageService;
        this.platformService = platformService;
        this.mobilePlatformService = mobilePlatformService;
        this.webPlatformService = webPlatformService;
        this.language = headers => {
            headers[HTTP_CONSTANTS.X_APPLICATION_LANGUAGE_HEADER] =
                this.languageService.getSelectedLanguage();
            return headers;
        };
        this.addXCallerChain = headers => {
            if (this.platformService.isReactNative()) {
                return (headers[HTTP_CONSTANTS.X_CALLER_CHAIN_HEADER] =
                    this.mobilePlatformService.getXCallerChainHeader());
            }
            else {
                return (headers[HTTP_CONSTANTS.X_CALLER_CHAIN_HEADER] =
                    this.webPlatformService.getXCallerChainHeader());
            }
        };
        this.headerFunctions = {
            language: this.language,
            etag: this.etag,
            userId: this.userId,
            proximusContentType: this.proximusContentType,
            encryptionKey: this.encryptionKey,
            customHeaders: this.customHeaders,
        };
    }
    /**
     * retrieves request required headers
     * @memberof proximus.core.entity:HeaderService
     * @param {String} http method
     * @param {Entity} entity to be processed
     * @returns {Promise} promise which resolves with request headers
     */
    getRequestHeaders(entity) {
        const deferred = defer();
        const headers = {};
        this.jwtService
            .addJwtHeaders(entity, headers)
            .then(headers => {
            this.setDefaultContentType(headers);
            this.addXCallerChain(headers);
            if (entity.requestHeaders) {
                entity.requestHeaders.forEach((headerFunctionString) => {
                    this.headerFunctions[headerFunctionString](headers, entity);
                });
            }
            this.enableEtag(entity, headers);
            deferred.resolve(headers);
        })
            .catch(error => {
            deferred.reject(error);
        });
        return deferred.promise;
    }
    /**
     * set entity properties from response headers
     * @memberof proximus.core.entity:HeaderService
     * @param {Entity} entity to set properties to
     * @param {Array} response headers
     * @returns {Entity} entity
     */
    setEntityPropertiesFromHeaders(entity, headers) {
        entity.etag = this.getEtag(headers);
        entity.expires = this.getExpires(entity, headers);
        entity.headers = headers;
        return entity;
    }
    enableEtag(entity, headers) {
        if (entity.enableEtag) {
            if ((typeof entity.enableEtag === 'boolean' && entity.enableEtag) ||
                (typeof entity.enableEtag === 'function' && entity.enableEtag())) {
                this.etag(headers, entity);
                if (entity.dataStrategy === DATASTRATEGY.NONE) {
                    entity.dataStrategy = DATASTRATEGY.STORAGE_PROXY;
                }
                if (entity.disableCache) {
                    entity.disableCache = false;
                }
            }
        }
    }
    getEtag(headers) {
        return headers[HTTP_CONSTANTS.ETAG_HEADER];
    }
    getExpires(entity, headers) {
        const expiries = typeof entity.expiresIn === 'number';
        return expiries
            ? new Date().getTime() + entity.expiresIn * 1000
            : this.getNextExpirationDate(headers);
    }
    getNextExpirationDate(headers) {
        let durationMillis = 0;
        if (headers[HTTP_CONSTANTS.CACHE_CONTROL_HEADER]) {
            const cacheControlHeaderValues = headers[HTTP_CONSTANTS.CACHE_CONTROL_HEADER].split(',');
            cacheControlHeaderValues.forEach(cacheControlHeaderValue => {
                if (cacheControlHeaderValue.indexOf('max-age') > -1) {
                    durationMillis = parseInt(cacheControlHeaderValue.split('=')[1], 10);
                }
            });
        }
        return new Date().getTime() + durationMillis * 1000;
    }
    customHeaders(headers, entity) {
        if (entity.customHeaders) {
            Object.keys(entity.customHeaders).forEach(headerName => {
                const headerValue = entity.customHeaders[headerName];
                if (headerValue === null || headerValue === '') {
                    delete headers[headerName];
                }
                else {
                    headers[headerName] = entity.customHeaders[headerName];
                }
            });
        }
        return headers;
    }
    userId(headers) {
        headers[HTTP_CONSTANTS.X_APPLICATION_USER_ID_HEADER] = true;
        return headers;
    }
    etag(headers, entity) {
        if (entity.etag) {
            headers[HTTP_CONSTANTS.IF_NONE_MATCH_HEADER] = entity.etag;
        }
        return headers;
    }
    encryptionKey(headers, entity) {
        if (entity.secure) {
            headers[HTTP_CONSTANTS.X_APPLICATION_ENCRYPTION_KEY_HEADER] = true;
        }
        return headers;
    }
    setDefaultContentType(headers) {
        headers[HTTP_CONSTANTS.CONTENT_TYPE_HEADER] = 'application/json';
        return headers;
    }
    //FIXME: name this content type since the header is called content type
    proximusContentType(headers, entity) {
        const namespace = entity.rel.substring(entity.rel.lastIndexOf(':') + 1, entity.rel.length);
        headers[HTTP_CONSTANTS.ACCEPT_HEADER] =
            'application/vnd.pxs.' + namespace.toLowerCase() + '.' + entity.version + '+json';
        return headers;
    }
}
HeaderService.ID = 'HeaderService';
export default HeaderService;
